/**
 * Surface outlines — the shape of a surface that is not a plain rectangle.
 *
 * A wall under a sloped ceiling is a pentagon (gable end) or a trapezoid (shed
 * end). The stored surface is still the rectangle `width × highest point`, and
 * everything on it is still a rectangle; `Surface.outline` (derived from the
 * room's ceiling profile at read time) says where the wall really stops. This
 * module answers the questions the maths asks about that boundary:
 *
 *   - how high is the wall at x?               `outlineTopAt`
 *   - how high at least / at most over a span? `outlineTopRange`
 *   - where along the wall is it taller than y? `outlineSpansAbove`
 *   - the straight pieces of the top edge       `outlineTopPieces`
 *   - how long is the top edge between two x?   `outlineRakeLength`
 *
 * Outlines are counter-clockwise from the bottom-left in surface coordinates,
 * with a level floor line and vertical sides — only the TOP edge is shaped.
 * (The functions work from the polygon's upper boundary, so a more elaborate
 * top — a hinge for a knee wall, say — needs nothing new here.)
 */

import { Surface } from '../../types/sections';

export type Outline = [number, number][];

const EPS = 1e-6;

/** The plain rectangle, as an outline. */
export function rectOutline(width: number, height: number): Outline {
  return [[0, 0], [width, 0], [width, height], [0, height]];
}

/** Is this outline just its bounding rectangle (a level top)? */
export function isRectOutline(outline: Outline): boolean {
  if (outline.length !== 4) return false;
  const ys = outline.map((p) => p[1]);
  const xs = outline.map((p) => p[0]);
  const top = Math.max(...ys);
  const bottom = Math.min(...ys);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  return outline.every(([x, y]) => (Math.abs(x - left) < EPS || Math.abs(x - right) < EPS) && (Math.abs(y - top) < EPS || Math.abs(y - bottom) < EPS));
}

/** The surface's outline when it is shaped; null for a plain rectangle (the common case). */
export function surfaceOutline(surface: Surface): Outline | null {
  return surface.outline && !isRectOutline(surface.outline) ? surface.outline : null;
}

/** One straight piece of the top edge, left to right. */
export interface TopPiece {
  x0: number;
  x1: number;
  /** Height at `x0` and at `x1`. */
  y0: number;
  y1: number;
}

/**
 * The upper boundary of the outline as straight pieces, left to right, one per
 * slab between neighbouring vertex x's. Within a slab the topmost edge of a
 * simple polygon is one edge, so each piece is straight.
 */
export function outlineTopPieces(outline: Outline): TopPiece[] {
  const xs = [...new Set(outline.map((p) => p[0]))].sort((a, b) => a - b);
  const pieces: TopPiece[] = [];
  const n = outline.length;
  for (let i = 0; i < xs.length - 1; i++) {
    const x0 = xs[i];
    const x1 = xs[i + 1];
    if (x1 - x0 < EPS) continue;
    const mid = (x0 + x1) / 2;
    // The edge lying highest over the middle of the slab.
    let best: { y: number; a: [number, number]; b: [number, number] } | null = null;
    for (let k = 0; k < n; k++) {
      const a = outline[k];
      const b = outline[(k + 1) % n];
      const lo = Math.min(a[0], b[0]);
      const hi = Math.max(a[0], b[0]);
      if (hi - lo < EPS || mid < lo || mid > hi) continue; // vertical, or not over the slab
      const y = a[1] + ((mid - a[0]) / (b[0] - a[0])) * (b[1] - a[1]);
      if (!best || y > best.y) best = { y, a, b };
    }
    if (!best) continue;
    const at = (x: number) => best!.a[1] + ((x - best!.a[0]) / (best!.b[0] - best!.a[0])) * (best!.b[1] - best!.a[1]);
    pieces.push({ x0, x1, y0: at(x0), y1: at(x1) });
  }
  return pieces;
}

function pieceAt(pieces: TopPiece[], x: number): TopPiece | null {
  // On a shared vertex x, either piece gives the same height.
  return pieces.find((p) => x >= p.x0 - EPS && x <= p.x1 + EPS) ?? null;
}

function heightOn(p: TopPiece, x: number): number {
  return p.x1 - p.x0 < EPS ? Math.max(p.y0, p.y1) : p.y0 + ((x - p.x0) / (p.x1 - p.x0)) * (p.y1 - p.y0);
}

/** The height of the wall at `x` (0 outside the outline's width). */
export function outlineTopAt(outline: Outline, x: number): number {
  const p = pieceAt(outlineTopPieces(outline), x);
  return p ? heightOn(p, x) : 0;
}

/** The least and greatest height of the wall over `[x0, x1]`. */
export function outlineTopRange(outline: Outline, x0: number, x1: number): { min: number; max: number } {
  const pieces = outlineTopPieces(outline);
  let min = Infinity;
  let max = -Infinity;
  const take = (y: number) => { min = Math.min(min, y); max = Math.max(max, y); };
  for (const p of pieces) {
    const a = Math.max(x0, p.x0);
    const b = Math.min(x1, p.x1);
    if (b - a < -EPS) continue;
    take(heightOn(p, a));
    take(heightOn(p, b));
  }
  if (min === Infinity) return { min: 0, max: 0 };
  return { min, max };
}

/** The stretches of `[x0, x1]` (the whole width by default) where the wall is taller than `y`. */
export function outlineSpansAbove(outline: Outline, y: number, x0 = -Infinity, x1 = Infinity): [number, number][] {
  const spans: [number, number][] = [];
  for (const p of outlineTopPieces(outline)) {
    const a = Math.max(x0, p.x0);
    const b = Math.min(x1, p.x1);
    if (b - a < EPS) continue;
    const ya = heightOn(p, a);
    const yb = heightOn(p, b);
    let from = a;
    let to = b;
    if (ya <= y + EPS && yb <= y + EPS) continue;
    if (ya <= y + EPS) from = a + ((y - ya) / (yb - ya)) * (b - a);
    else if (yb <= y + EPS) to = a + ((y - ya) / (yb - ya)) * (b - a);
    if (to - from < EPS) continue;
    const last = spans[spans.length - 1];
    if (last && Math.abs(last[1] - from) < EPS) last[1] = to;
    else spans.push([from, to]);
  }
  return spans;
}

/**
 * The x's inside `(x0, x1)` where the top edge bends or crosses one of the
 * `levels` — between two neighbouring breaks the edge is straight and wholly
 * above, below or between the levels.
 */
export function outlineBreaks(outline: Outline, x0: number, x1: number, levels: number[]): number[] {
  const out = new Set<number>();
  const add = (x: number) => { if (x > x0 + EPS && x < x1 - EPS) out.add(x); };
  for (const p of outlineTopPieces(outline)) {
    add(p.x0);
    add(p.x1);
    if (Math.abs(p.y1 - p.y0) < EPS) continue;
    for (const level of levels) {
      const t = (level - p.y0) / (p.y1 - p.y0);
      if (t > EPS && t < 1 - EPS) add(p.x0 + t * (p.x1 - p.x0));
    }
  }
  return [...out].sort((a, b) => a - b);
}

/** The length of the top edge between `x0` and `x1` — the trim a rake takes. */
export function outlineRakeLength(outline: Outline, x0: number, x1: number): number {
  let length = 0;
  for (const p of outlineTopPieces(outline)) {
    const a = Math.max(x0, p.x0);
    const b = Math.min(x1, p.x1);
    if (b - a < EPS) continue;
    length += Math.hypot(b - a, heightOn(p, b) - heightOn(p, a));
  }
  return length;
}
